import { NewRadio } from '../../PIM/common';
import type { BombiixCellRendererParams, BombiixRowId } from '../types';

interface RadioGroupOption {
  _id?: unknown;
  name?: string;
  label?: string;
}

interface RadioGroupCellExtras {
  /** Options rendered as one radio each. The cell stores the selected option's id. */
  options?: RadioGroupOption[];
  getOptionLabel?: (opt: RadioGroupOption) => string;
  getOptionValue?: (opt: RadioGroupOption) => string;
}

type RadioGroupCellProps = BombiixCellRendererParams & RadioGroupCellExtras;

/**
 * Radio-group cell: one radio per option, all within a single cell, storing the chosen option's id.
 *
 * Distinct from RadioCell, which places ONE radio per row and uses them to pick a row. This is for
 * an attribute whose type is genuinely "radio button" — the options belong to the cell, not the
 * grid. Options lay out in a single non-wrapping row that scrolls horizontally when they exceed the
 * column width, matching how the multi-select cell handles overflowing chips; the fixed cell height
 * means wrapping would simply be clipped.
 */
function RadioGroupCell(params: RadioGroupCellProps) {
  const { value, data, colDef, options = [], getOptionLabel, getOptionValue, onRowChange, getRowId, disabled } = params;

  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const labelFn = getOptionLabel || ((opt: RadioGroupOption) => String(opt?.name ?? opt?.label ?? ''));
  const valueFn = getOptionValue || ((opt: RadioGroupOption) => String(opt?._id ?? ''));

  /* The stored value may be the bare id or the whole option object, depending on which screen last
     wrote it — normalise before comparing so the correct radio shows as checked either way. */
  const selectedId =
    value !== null && typeof value === 'object' ? valueFn(value as RadioGroupOption) : value !== undefined && value !== null ? String(value) : null;

  /* Group name must be unique per row AND column, otherwise every row's radios share one group and
     selecting in one row clears another. */
  const groupName = `cag-radio-${String(rowId ?? 'row')}-${field ?? 'field'}`;

  return (
    <div className="cag-cell">
      <div className="cag-radio-group">
        {options.map((option) => {
          const optionId = valueFn(option);
          return (
            <NewRadio
              key={optionId}
              id={`${groupName}-${optionId}`}
              name={groupName}
              label={labelFn(option)}
              checked={selectedId === optionId}
              disabled={Boolean(disabled)}
              onChange={() => {
                if (rowId !== undefined && field) onRowChange?.(rowId, field, optionId);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export default RadioGroupCell;
